import "../../styles/adventure-scene.css";
import LlAudioSearchCombo from "./ll-audio-search-combo.marko";
import LlPillSearch from "./ll-pill-search.marko";
import {
  joinLines,
  getCharName,
  renderInteractiveLines,
  wrapWordsForTranslation,
  toDisplayText,
  mergeTaGameLexiconUi,
} from "../../lib/scene-render.js";
import { resolveAdventureImgBase } from "../../lib/resolve-adventure-img-base.js";
import { getAssetCdnBase, getAssetCdnPathPrefix, toAbsoluteCdnPath } from "../../lib/cdn-base.js";
import { heroCoverAttrsFromPublicPath } from "../../lib/responsive-story-images.js";
import { sceneHotspotsStaticJsonPath } from "../../lib/scene-static-api-paths.js";
import { lexiconApiSpeakLangForUi } from "../../lib/lexicon-api-speak-lang.js";
import { lexiconOccurrencesApiPath } from "../../lib/lexicon-api-paths.js";
import PremiumPaywallCard from "./premium-paywall-card.marko";
import TaChromeCloseButton from "../ta-chrome-close-button.marko";
import LlReaderPdfBtn from "./ll-reader-pdf-btn.marko";

<if=!input.data.scene>
  <div class="container">
    <div class="empty">
      <div class="empty__icon">🗺️</div>
      <p class="empty__title">${input.data.locale.notFoundTitle}</p>
      <a href=`${input.data.basePath}/${input.data.adventure?.slug || ""}` class="btn btn--outline" style="margin-top:var(--space-md)">← ${input.data.locale.backLabel}</a>
    </div>
  </div>
</if>
<else-if=input.data.scene.textAdventure>
  <div class="ta-layout ll-zipf-scope" id="ta-root">
    <div class="ta-header">
      <a href=`${input.data.basePath}/${input.data.adventure.slug}` class="adventure-header__back" style="display:inline-flex; align-items:center; gap:var(--space-sm)">
        ← ${toDisplayText(input.data.adventure.targetTitle || input.data.adventure.title, input.data.adventure.slug || "")}
        <span class=`badge badge--${(input.data.adventure.level || "").toLowerCase()}`>${input.data.adventure.level}</span>
      </a>
      <div style="margin-left:auto; display:flex; align-items:center; gap:var(--space-sm)">
        <span id="ta-header-scene-title" style="font-size:0.8rem; color:var(--color-text-muted)">${toDisplayText(input.data.scene.learnerTitle || input.data.textAdventureData?.sceneTitle || input.data.scene.targetTitle || input.data.scene.title, input.data.scene.slug || "")}</span>
        <button class="ta-reset" id="ta-reset-btn" title=input.data.locale.resetTitle>↺ ${input.data.locale.resetLabel || input.data.locale.resetTitle}</button>
      </div>
    </div>

    <div class="ta-main" id="ta-main">
      <if=input.data.scene.learnerTitle>
        <p class="title-aid title-aid--detail" id="ta-scene-learner-title">${toDisplayText(input.data.scene.learnerTitle, input.data.scene.slug || "")}</p>
      </if>
      <div class="reader-title-row" style="margin-bottom:var(--space-xl)">
        <h1 style="font-size:1.5rem; font-weight:700; letter-spacing:-0.02em; margin:0" id="ta-scene-title">
          $!{wrapWordsForTranslation(
            toDisplayText(
              input.data.textAdventureData?.sceneTitle || input.data.scene.targetTitle || input.data.scene.title,
              input.data.scene.slug || "",
            ),
          )}
        </h1>
        <if=input.data.pdfUrl>
          <LlReaderPdfBtn
            pdfUrl=input.data.pdfUrl
            title=input.data.downloadPdfLabel
            ariaLabel=input.data.downloadPdfAriaLabel
            label=input.data.pdfAbbrevLabel
          />
        </if>
      </div>

      <if=input.data.sceneImagePath>
        <div class="ta-visual-stage" id="ta-visual-stage">
          <if=input.data.allowHotspotEditor>
            <div class="ta-hotspot-mode-bar" id="ta-hotspot-mode-bar">
              <div class="ta-hotspot-mode-bar__toggle" role="group" aria-label=(input.data.textAdventureData?.ui?.hotspotModeAriaLabel || "")>
                <button type="button" class="ta-hotspot-mode-bar__mode-btn ta-hotspot-mode-bar__mode-btn--active" id="ta-hotspot-mode-normal" aria-pressed="true">
                  ${input.data.textAdventureData?.ui?.hotspotViewLabel || ""}
                </button>
                <button type="button" class="ta-hotspot-mode-bar__mode-btn" id="ta-hotspot-mode-edit" aria-pressed="false">
                  ${input.data.textAdventureData?.ui?.hotspotEditLabel || ""}
                </button>
              </div>
              <button type="button" class="btn btn--sm btn--primary ta-hotspot-mode-bar__save" id="ta-hotspot-mode-save" hidden>
                ${input.data.textAdventureData?.ui?.hotspotSaveLabel || ""}
              </button>
            </div>
          </if>
          <div
            class="ta-fs-land-inset-gauge"
            id="ta-fs-land-inset-gauge"
            aria-hidden="true"
          />
          <div class="ta-visual-stage__media">
            <div class="ta-visual-stage__scene-stack">
              <div class="ta-visual-stage__inv-pad">
                <div
                  class="ta-visual-inventory-float"
                  id="ta-visual-inventory-float"
                  aria-label=(input.data.textAdventureData?.ui?.inventoryAriaLabel || "")
                >
                  <div class="ta-visual-inventory-float__inner">
                    <div class="ta-visual-inventory-float__items" id="ta-visual-inventory-items"></div>
                  </div>
                </div>
              </div>
              <div class="ta-visual-stage__image-wrap" id="ta-visual-image-wrap">
                <for|hero| of=[heroCoverAttrsFromPublicPath(input.data.sceneImagePath)]>
                  <img
                    class="ta-visual-stage__img"
                    id="ta-visual-stage-img"
                    src=hero.src
                    data-ll-hero-progressive=hero.progressiveJson
                    alt=(input.data.textAdventureData?.sceneTitle || input.data.scene.targetTitle || input.data.scene.title)
                    width=hero.width
                    height=hero.height
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    crossorigin=(getAssetCdnBase() ? "anonymous" : null)
                    onError="this.style.display='none'"
                  >
                </for>
                <div class="ta-scene-transition-overlay" id="ta-scene-transition-overlay" hidden aria-hidden="true">
                  <video class="ta-scene-transition-overlay__video" playsinline preload="metadata"></video>
                </div>
                <div class="ta-visual-stage__hotspots" id="ta-scene-hotspots" aria-hidden="true"></div>
                <div class="ta-hotspot-edit-layer" id="ta-hotspot-edit-layer" hidden></div>
                <div class="ta-visual-stage__bottombar">
                  <button
                    type="button"
                    class="audio-simple-btn ta-visual-stage__play js-audio-btn"
                    id="ta-visual-audio-btn"
                    data-audio-simple="1"
                    data-audio-src=(input.data.sceneAudioSrc || "")
                    data-audio-debug=(input.data.sceneAudioDebugMessage || "")
                    title=(input.data.locale.audioBadgeLabel || "")
                    aria-label=(input.data.locale.audioBadgeLabel || "")
                  >
                  </button>
                  <div class="ta-visual-stage__trailing">
                    <div class="ta-visual-stage__settings-wrap">
                      <button
                        type="button"
                        class="ta-visual-stage__icon-btn"
                        id="ta-visual-settings-btn"
                        aria-haspopup="true"
                        aria-expanded="false"
                        title=(input.data.readerChromeLabels?.settings || input.data.textAdventureData?.ui?.settingsLabel)
                      >⚙</button>
                      <div class="ta-visual-stage__dropdown" id="ta-visual-settings-dropdown" hidden role="menu">
                        <label class="ta-visual-stage__menu-check">
                          <input type="checkbox" id="ta-visual-graphics-toggle">
                          <span id="ta-visual-graphics-label">${input.data.readerChromeLabels?.graphics || input.data.textAdventureData?.ui?.graphicsModeLabel}</span>
                        </label>
                        <label class="ta-visual-stage__menu-check">
                          <input type="checkbox" id="ta-visual-subtitles-toggle" checked>
                          <span id="ta-visual-subtitles-label">${input.data.readerChromeLabels?.subtitles || input.data.textAdventureData?.ui?.subtitlesLabel}</span>
                        </label>
                        <label class="ta-visual-stage__menu-check">
                          <input type="checkbox" id="ta-visual-video-transitions-toggle">
                          <span id="ta-visual-video-transitions-label">${input.data.textAdventureData?.ui?.videoSceneTransitionsLabel || ""}</span>
                        </label>
                      </div>
                    </div>
                    <button type="button" class="ta-visual-stage__icon-btn" id="ta-visual-fs-btn" title=(input.data.readerChromeLabels?.fullscreen || input.data.textAdventureData?.ui?.fullscreenLabel)>⛶</button>
                  </div>
                </div>
              </div>
              <div class="ta-visual-stage__subtitle-cluster">
                <button type="button" class="ta-visual-stage__sub-btn" id="ta-visual-sub-prev" title=(input.data.textAdventureData?.ui?.previousLineLabel || "")>←</button>
                <div class="ta-visual-stage__subtitle-line" id="ta-visual-subtitle-line" aria-live="polite"></div>
                <button type="button" class="ta-visual-stage__sub-btn" id="ta-visual-sub-next" title=(input.data.textAdventureData?.ui?.nextLineLabel || "")>→</button>
              </div>
            </div>
          </div>
          <div class="ta-fs-infobox-overlay" id="ta-fs-infobox-overlay" aria-hidden="true"></div>
          <div class="ta-fs-infobox-mount" id="ta-fs-infobox-mount" aria-hidden="true"></div>
          <div class="ta-hotspot-name-tooltip" id="ta-hotspot-name-tooltip"></div>
          <div class="ta-dialog-section ta-dialog-section--hidden ta-dialog-section--in-stage" id="ta-dialog-section" aria-hidden="true">
            <div class="ta-dialog-backdrop" id="ta-dialog-backdrop"></div>
            <div
              class="ta-dialog-modal"
              id="ta-dialog-modal"
              role="dialog"
              aria-modal="true"
              aria-label=(input.data.textAdventureData?.ui?.dialogAriaLabel || "")
            >
              <div class="ta-dialog-modal__body-stack">
                <div class="ta-dialog-modal__media">
                  <div class="ta-dialog-modal__avatar" id="ta-dialog-avatar-wrap">
                    <img
                      class="ta-dialog-modal__avatar-img"
                      id="ta-dialog-avatar-img"
                      alt=""
                      width="320"
                      height="320"
                      style="display:none"
                    >
                    <video
                      class="ta-dialog-modal__avatar-video"
                      id="ta-dialog-avatar-video"
                      playsinline
                      muted
                      preload="metadata"
                      style="display:none"
                      width="320"
                      height="480"
                      aria-hidden="true"
                    />
                    <div class="ta-dialog-modal__avatar-fallback" id="ta-dialog-avatar-fallback">💬</div>
                  </div>
                </div>
                <div class="ta-dialog-modal__body-row">
                  <div class="ta-dialog-modal__center">
                    <div class="ta-dialog-header-audio ta-dialog-header-audio--empty" id="ta-dialog-header">
                      <div id="ta-dialog-audio-dock" class="ta-dialog-audio-dock">
                        <button
                          type="button"
                          class="audio-simple-btn ta-dialog-bubble__audio-play js-audio-btn"
                          id="ta-dialog-audio-btn"
                          data-audio-simple="1"
                          data-audio-src=""
                          title=(input.data.locale.audioBadgeLabel || "")
                          aria-label=(input.data.locale.audioBadgeLabel || "")
                          style="display:none"
                        ></button>
                      </div>
                    </div>
                    <div class="ta-dialog-lines-wrap">
                      <div class="ta-dialog-lines" id="ta-dialog-lines" aria-live="polite"></div>
                    </div>
                  </div>
                  <div class="ta-dialog-modal__actions-col">
                    <TaChromeCloseButton
                      id="ta-dialog-close-btn"
                      title=(input.data.textAdventureData?.ui?.closeDialogLabel || "")
                      ariaLabel=(input.data.textAdventureData?.ui?.closeDialogLabel || "")
                      extraClass="ta-dialog-header__close ta-dialog-nav"
                    />
                    <div class="ta-dialog-modal__actions-col__nav">
                      <button type="button" class="ta-dialog-nav ta-dialog-nav--up ta-round-chrome-btn" id="ta-dialog-nav-up" aria-label=(input.data.textAdventureData?.ui?.previousLineLabel || "")>↑</button>
                      <button type="button" class="ta-dialog-nav ta-dialog-nav--down ta-round-chrome-btn" id="ta-dialog-nav-down" aria-label=(input.data.textAdventureData?.ui?.nextLineLabel || "")>↓</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ta-fs-restore-banner" id="ta-fs-restore-banner" hidden>
          <button type="button" class="ta-fs-restore-banner__btn" id="ta-fs-restore-btn">${input.data.textAdventureData?.ui?.restoreFullscreenLabel || ""}</button>
        </div>
      </if>

      <if=!input.data.sceneImagePath>
        <LlAudioSearchCombo
          comboClass="ta-scene-controls"
          audioButtonClass="audio-mini-btn ta-scene-controls__play js-audio-btn"
          searchShellClass="ta-sidebar-search ll-scene-controls__search ll-wortschatz__search-wrap ll-wortschatz__search-wrap--reader-combo"
          audioSrc=(input.data.sceneAudioSrc || "")
          audioDurationHint=(input.data.sceneAudioDurationHint != null ? input.data.sceneAudioDurationHint : "")
          audioDebug=(input.data.sceneAudioDebugMessage || "")
          audioTitle=(input.data.locale.audioBadgeLabel || "")
          audioAriaLabel=(input.data.locale.audioBadgeLabel || "")
          searchId="ta-scene-search-input"
          searchPlaceholder=(input.data.textAdventureData?.ui?.sceneSearchPlaceholder || "")
          searchAriaLabel=(input.data.textAdventureData?.ui?.sceneSearchAriaLabel || "")
        />
      </if>
      <else>
        <div class="ta-scene-controls ta-scene-controls--search-only">
          <LlPillSearch
            id="ta-scene-search-input"
            placeholder=(input.data.textAdventureData?.ui?.sceneSearchPlaceholder || "")
            searchAriaLabel=(input.data.textAdventureData?.ui?.sceneSearchAriaLabel || "")
            shellClass="ta-sidebar-search ll-scene-controls__search ll-wortschatz__search-wrap ll-wortschatz__search-wrap--search-only-row"
            inputClass="ll-search-input ll-wortschatz__search-input"
          />
        </div>
      </else>

      <div class="ll-zipf-panel ll-zipf-panel--scene">
        <label class="ll-zipf-toggle">
          <input type="checkbox" id="ll-zipf-highlight-toggle"/>
          <span class="ll-zipf-toggle__text">${input.data.textToolsLabels?.zipfHighlightLabel || ""}</span>
        </label>
        <div class="ll-zipf-source" role="group" aria-labelledby="ll-zipf-freq-legend-ta">
          <div class="ll-zipf-source__legend" id="ll-zipf-freq-legend-ta">${input.data.textToolsLabels?.lexiconFrequencySourceGroupLabel || ""}</div>
          <div class="ll-zipf-source__radios">
            <label class="ll-zipf-source__opt">
              <input type="radio" name="ll-lexicon-frequency-source" value="combined"/>
              <span>${input.data.textToolsLabels?.lexiconFrequencyCombinedLabel || ""}</span>
            </label>
            <label class="ll-zipf-source__opt">
              <input type="radio" name="ll-lexicon-frequency-source" id="ll-lexicon-frequency-wordfreq" value="wordfreq"/>
              <span>${input.data.textToolsLabels?.lexiconFrequencyWordfreqLabel || ""}</span>
            </label>
            <label class="ll-zipf-source__opt">
              <input type="radio" name="ll-lexicon-frequency-source" id="ll-lexicon-frequency-corpus" value="corpus"/>
              <span>${input.data.textToolsLabels?.lexiconFrequencyCorpusLabel || ""}</span>
            </label>
            <label class="ll-zipf-source__opt">
              <input type="radio" name="ll-lexicon-frequency-source" value="dwds"/>
              <span>${input.data.textToolsLabels?.lexiconFrequencyDwdsLabel || ""}</span>
            </label>
          </div>
        </div>
        <div class="ll-zipf-legend" id="ll-zipf-legend" hidden aria-hidden="true">
          <p class="ll-zipf-legend__title">${input.data.textToolsLabels?.zipfLegendTitle || ""}</p>
          <ul class="ll-zipf-legend__list" id="ll-zipf-legend-list"></ul>
        </div>
      </div>

      <div class="ta-scene-text reader-text" id="ta-scene-text">
        <for|block| of=(input.data.scene.script || []).filter((b) => b.type !== "options")>
          <if=block.type === "narration">
            <p class="ta-paragraph">$!{renderInteractiveLines(block.text)}</p>
          </if>
          <else-if=block.type === "dialog">
            <div class="scene-block scene-block--dialog">
              <div class="scene-block__speaker">${getCharName(block.character, input.data.adventure?.characters)}</div>
              <div class="scene-block__bubble">
                <p class="scene-block__text">$!{renderInteractiveLines(block.text)}</p>
              </div>
            </div>
          </else-if>
        </for>
      </div>

      <if=input.data.sceneImagePath>
        <div id="ta-dialog-root-below" class="ta-dialog-root-below" aria-hidden="true"></div>
      </if>

      <if=!input.data.sceneImagePath>
        <div class="ta-dialog-section ta-dialog-section--hidden" id="ta-dialog-section" aria-hidden="true">
          <div class="ta-dialog-backdrop" id="ta-dialog-backdrop"></div>
          <div
            class="ta-dialog-modal"
            id="ta-dialog-modal"
            role="dialog"
            aria-modal="true"
            aria-label=(input.data.textAdventureData?.ui?.dialogAriaLabel || "")
          >
            <div class="ta-dialog-modal__body-stack">
              <div class="ta-dialog-modal__media">
                <div class="ta-dialog-modal__avatar" id="ta-dialog-avatar-wrap">
                  <img
                    class="ta-dialog-modal__avatar-img"
                    id="ta-dialog-avatar-img"
                    alt=""
                    width="320"
                    height="320"
                    style="display:none"
                  >
                  <video
                    class="ta-dialog-modal__avatar-video"
                    id="ta-dialog-avatar-video"
                    playsinline
                    muted
                    preload="metadata"
                    style="display:none"
                    width="320"
                    height="480"
                    aria-hidden="true"
                  />
                  <div class="ta-dialog-modal__avatar-fallback" id="ta-dialog-avatar-fallback">💬</div>
                </div>
              </div>
              <div class="ta-dialog-modal__body-row">
                <div class="ta-dialog-modal__center">
                  <div class="ta-dialog-header-audio ta-dialog-header-audio--empty" id="ta-dialog-header">
                    <div id="ta-dialog-audio-dock" class="ta-dialog-audio-dock">
                      <button
                        type="button"
                        class="audio-simple-btn ta-dialog-bubble__audio-play js-audio-btn"
                        id="ta-dialog-audio-btn"
                        data-audio-simple="1"
                        data-audio-src=""
                        title=(input.data.locale.audioBadgeLabel || "")
                        aria-label=(input.data.locale.audioBadgeLabel || "")
                        style="display:none"
                      ></button>
                    </div>
                  </div>
                  <div class="ta-dialog-lines-wrap">
                    <div class="ta-dialog-lines" id="ta-dialog-lines" aria-live="polite"></div>
                  </div>
                </div>
                <div class="ta-dialog-modal__actions-col">
                  <TaChromeCloseButton
                    id="ta-dialog-close-btn"
                    title=(input.data.textAdventureData?.ui?.closeDialogLabel || "")
                    ariaLabel=(input.data.textAdventureData?.ui?.closeDialogLabel || "")
                    extraClass="ta-dialog-header__close ta-dialog-nav"
                  />
                  <div class="ta-dialog-modal__actions-col__nav">
                    <button type="button" class="ta-dialog-nav ta-dialog-nav--up ta-round-chrome-btn" id="ta-dialog-nav-up" aria-label=(input.data.textAdventureData?.ui?.previousLineLabel || "")>↑</button>
                    <button type="button" class="ta-dialog-nav ta-dialog-nav--down ta-round-chrome-btn" id="ta-dialog-nav-down" aria-label=(input.data.textAdventureData?.ui?.nextLineLabel || "")>↓</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </if>

      <div class="ta-ending ta-ending--hidden" id="ta-ending">
        <div class="ta-ending__title" id="ta-ending-title">${input.data.locale.endingTitle}</div>
        <div class="ta-ending__text" id="ta-ending-text"></div>
        <a href=`${input.data.basePath}/${input.data.adventure.slug}` class="btn btn--primary" id="ta-ending-back">${input.data.locale.endingBackLabel}</a>
      </div>

      <!-- Label + options are inserted by adventure-scene-client when scene.options is non-empty -->
      <div class="ta-options" id="ta-options"></div>
    </div>

    <div class="ta-sidebar">
      <div
        class="ta-sidebar-inventory"
        id="ta-sidebar-inventory"
        aria-label=(input.data.textAdventureData?.ui?.inventoryTitle || "")
      >
        <div class="ta-sidebar-inventory__label" id="ta-inv-label-wrap" hidden>
          <span id="ta-inv-label-text">${input.data.textAdventureData?.ui?.inventoryTitle || ""}</span>
        </div>
        <div class="ta-sidebar-inventory__items" id="ta-sidebar-inventory-items">
          <span class="ta-sidebar-inventory__empty" id="ta-sidebar-inventory-empty">${input.data.textAdventureData?.ui?.inventoryEmpty || ""}</span>
        </div>
      </div>

      <div class="ta-infobox ta-infobox--hint" id="ta-infobox">
        <TaChromeCloseButton
          id="ta-infobox-close-btn"
          title=(input.data.textAdventureData?.ui?.infoboxCloseLabel || "")
          ariaLabel=(input.data.textAdventureData?.ui?.infoboxCloseLabel || "")
        />
        <div class="ta-infobox__img-wrap" id="ta-infobox-img-wrap">
          <img class="ta-infobox__img" id="ta-infobox-img" src="" alt="" style="display:none" onerror="this.style.display='none';this.parentElement.querySelector('.ta-infobox__img-placeholder').style.display='block'">
          <div class="ta-infobox__img-placeholder" id="ta-infobox-placeholder">❓</div>
        </div>
        <div class="ta-infobox__body">
          <div class="ta-infobox__type" id="ta-infobox-type"></div>
          <div class="ta-infobox__name-row">
            <div class="ta-infobox__audio-row" id="ta-infobox-audio-row" hidden>
              <button
                type="button"
                class="audio-simple-btn ta-dialog-bubble__audio-play js-audio-btn"
                id="ta-infobox-audio-btn"
                data-audio-simple="1"
                data-audio-src=""
                title=(input.data.locale.audioBadgeLabel || "")
                aria-label=(input.data.locale.audioBadgeLabel || "")
              ></button>
            </div>
            <div class="ta-infobox__name" id="ta-infobox-name">${input.data.locale.hintText}</div>
          </div>
          <div class="ta-infobox__desc-block" id="ta-infobox-desc-block">
            <div class="ta-infobox__desc" id="ta-infobox-desc"></div>
          </div>
          <div class="ta-infobox__actions" id="ta-infobox-actions"></div>
        </div>
      </div>
    </div>
  </div>

  <div class="ta-notification-overlay" id="ta-notification-overlay">
    <div class="ta-notification" id="ta-notification">
      <div class="ta-notification__icon" id="ta-notification-icon">💡</div>
      <div class="ta-notification__body">
        <button
          type="button"
          class="ta-notification__close ta-round-chrome-btn"
          id="ta-notification-close"
          title=(input.data.textAdventureData?.ui?.infoboxCloseLabel || "")
          aria-label=(input.data.textAdventureData?.ui?.infoboxCloseLabel || "")
        >✕</button>
        <div class="ta-notification__text" id="ta-notification-text"></div>
      </div>
    </div>
  </div>

  <div class="ta-translate-tooltip" id="ta-translate-tooltip">
    <span class="ta-translate-tooltip__original" id="ta-tooltip-original"></span>
    <span class="ta-translate-tooltip__translated" id="ta-tooltip-translated"></span>
    <span class="ta-translate-tooltip__hint" id="ta-tooltip-hint" hidden></span>
  </div>

  <html-script id="ta-game-data" type="application/json">
    $!{JSON.stringify(
      Object.assign({}, input.data.textAdventureData || {}, {
        ui: mergeTaGameLexiconUi(
          input.data.textToolsLabels || {},
          (input.data.textAdventureData && input.data.textAdventureData.ui) || {},
        ),
        premiumGate: input.data.premiumGate || null,
        sceneImagePath: input.data.sceneImagePath || "",
        /** Same as `getAssetCdnBase()` on the server so the client can add Bunny `width`/`height` to `/img/...` URLs. */
        assetCdnBase: getAssetCdnBase() || "",
        assetCdnPathPrefix: getAssetCdnPathPrefix() || "",
        adventureImgBase: toAbsoluteCdnPath(resolveAdventureImgBase(input.data.adventure)),
        sceneAudioSrc: input.data.sceneAudioSrc || "",
        sceneScript: input.data.sceneScript || null,
        sceneAudioDurationHint: input.data.sceneAudioDurationHint != null ? input.data.sceneAudioDurationHint : "",
        scriptBlocks: (input.data.scene.script || []).filter((b) => b.type !== "options"),
        characters: input.data.adventure?.characters || [],
        sceneNav: {
          basePath: input.data.basePath || "",
          adventureSlug: (input.data.adventure && input.data.adventure.slug) || "",
          sceneIdToPath: input.data.sceneIdToSlug || {},
        },
        sceneHotspotsUrl: sceneHotspotsStaticJsonPath(input.data.targetLang || "", input.data.scene.contentId || ""),
      }),
    )}
  </html-script>
  <html-script src="/js/adventure-scene-client.js" defer></html-script>

  </else-if>
<else>
  <div class="adventure-wrap ll-zipf-scope">
    <div class="adventure-header">
      <a href=`${input.data.basePath}/${input.data.adventure.slug}` class="adventure-header__back" style="display:inline-flex; align-items:center; gap:var(--space-sm)">
        ← ${toDisplayText(input.data.adventure.targetTitle || input.data.adventure.title, input.data.adventure.slug || "")}
        <span class=`badge badge--${(input.data.adventure.level || "").toLowerCase()}`>${input.data.adventure.level}</span>
      </a>
      <div style="margin-left:auto; display:flex; align-items:center; gap:var(--space-sm)">
        <span style="font-size:0.8rem; color:var(--color-text-muted)">${toDisplayText(input.data.scene.learnerTitle || input.data.scene.targetTitle || input.data.scene.title, input.data.scene.slug || "")}</span>
      </div>
    </div>

    <if=input.data.sceneImagePath>
      <div class="ll-adventure-scene__visual" id="ll-adventure-scene-root">
        <div class="ll-adventure-scene__image-wrap" id="ll-adventure-scene-image-wrap">
          <for|hero| of=[heroCoverAttrsFromPublicPath(input.data.sceneImagePath)]>
            <img
              class="ll-adventure-scene__img"
              src=hero.src
              data-ll-hero-progressive=hero.progressiveJson
              alt=(input.data.scene.targetTitle || input.data.scene.title)
              width=hero.width
              height=hero.height
              loading="eager"
              fetchpriority="high"
              decoding="async"
              crossorigin=(getAssetCdnBase() ? "anonymous" : null)
              onError="this.style.display='none'"
            >
          </for>
          <div class="ll-adventure-scene__hotspots" id="ll-scene-hotspots" aria-hidden="true"></div>
        </div>
        <div class="ll-scene-controls ll-scene-controls--below-image ll-scene-controls--adventure-visual">
          <button
            type="button"
            class="audio-mini-btn ll-scene-controls__play js-audio-btn"
            id="ll-scene-audio-btn"
            data-audio-src=(input.data.sceneAudioSrc || "")
            data-audio-debug=(input.data.sceneAudioDebugMessage || "")
            title=(input.data.locale.audioBadgeLabel || "")
            aria-label=(input.data.locale.audioBadgeLabel || "")
          >
            <span class="audio-mini-btn__icon"><svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor"><path d="M7 4.5v15a1.5 1.5 0 0 0 2.3 1.2l11-7.5a1.5 1.5 0 0 0 0-2.4l-11-7.5A1.5 1.5 0 0 0 7 4.5z"/></svg></span>
            <span class="audio-mini-btn__track"><span class="audio-mini-btn__fill"></span><span class="audio-mini-btn__knob"></span></span>
            <span class="audio-mini-btn__turtle-zone js-turtle-zone" role="slider" tabindex="0" aria-valuemin="0.5" aria-valuemax="1" aria-valuenow="0.8">
              <span class="audio-mini-btn__turtle-speed" aria-hidden="true">×0.8</span>
              <span class="audio-mini-btn__turtle-track">
                <span class="audio-mini-btn__turtle-mark" aria-hidden="true">🐢</span>
              </span>
            </span>
          </button>
          <div class="ll-scene-controls__subtitle-line" id="ll-scene-subtitle-line" aria-live="polite"></div>
          <div class="ll-scene-controls__subtitle-nav">
            <button type="button" id="ll-scene-sub-prev" class="ll-scene-controls__sub-btn">←</button>
            <button type="button" id="ll-scene-sub-next" class="ll-scene-controls__sub-btn">→</button>
          </div>
          <button type="button" id="ll-scene-cc-toggle" class="ll-scene-controls__cc" aria-pressed="true" title=(input.data.textAdventureData?.ui?.closedCaptionsLabel || "") aria-label=(input.data.textAdventureData?.ui?.closedCaptionsLabel || "")>CC</button>
        </div>
      </div>
    </if>
    <else>
      <div class="ll-adventure-scene__visual ll-adventure-scene__visual--no-image" id="ll-adventure-scene-root">
        <div class="ll-scene-controls ll-scene-controls--below-image ll-scene-controls--adventure-visual">
          <button
            type="button"
            class="audio-mini-btn ll-scene-controls__play js-audio-btn"
            id="ll-scene-audio-btn"
            data-audio-src=(input.data.sceneAudioSrc || "")
            data-audio-debug=(input.data.sceneAudioDebugMessage || "")
            title=(input.data.locale.audioBadgeLabel || "")
            aria-label=(input.data.locale.audioBadgeLabel || "")
          >
            <span class="audio-mini-btn__icon"><svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor"><path d="M7 4.5v15a1.5 1.5 0 0 0 2.3 1.2l11-7.5a1.5 1.5 0 0 0 0-2.4l-11-7.5A1.5 1.5 0 0 0 7 4.5z"/></svg></span>
            <span class="audio-mini-btn__track"><span class="audio-mini-btn__fill"></span><span class="audio-mini-btn__knob"></span></span>
            <span class="audio-mini-btn__turtle-zone js-turtle-zone" role="slider" tabindex="0" aria-valuemin="0.5" aria-valuemax="1" aria-valuenow="0.8">
              <span class="audio-mini-btn__turtle-speed" aria-hidden="true">×0.8</span>
              <span class="audio-mini-btn__turtle-track">
                <span class="audio-mini-btn__turtle-mark" aria-hidden="true">🐢</span>
              </span>
            </span>
          </button>
          <div class="ll-scene-controls__subtitle-line" id="ll-scene-subtitle-line" aria-live="polite"></div>
          <div class="ll-scene-controls__subtitle-nav">
            <button type="button" id="ll-scene-sub-prev" class="ll-scene-controls__sub-btn">←</button>
            <button type="button" id="ll-scene-sub-next" class="ll-scene-controls__sub-btn">→</button>
          </div>
          <button type="button" id="ll-scene-cc-toggle" class="ll-scene-controls__cc" aria-pressed="true" title=(input.data.textAdventureData?.ui?.closedCaptionsLabel || "") aria-label=(input.data.textAdventureData?.ui?.closedCaptionsLabel || "")>CC</button>
        </div>
      </div>
    </else>

    <if=input.data.scene.learnerTitle>
      <p class="title-aid title-aid--detail">${toDisplayText(input.data.scene.learnerTitle, input.data.scene.slug || "")}</p>
    </if>
    <div class="reader-title-row" style="margin-bottom:var(--space-xl)">
      <h1 style="font-size:1.5rem; font-weight:700; letter-spacing:-0.02em; margin:0">
        ${toDisplayText(input.data.scene.targetTitle || input.data.scene.title, input.data.scene.slug || "")}
      </h1>
      <if=input.data.pdfUrl>
        <LlReaderPdfBtn
          pdfUrl=input.data.pdfUrl
          title=input.data.downloadPdfLabel
          ariaLabel=input.data.downloadPdfAriaLabel
          label=input.data.pdfAbbrevLabel
        />
      </if>
      <if=!input.data.sceneImagePath>
        <button
          type="button"
          class="audio-mini-btn js-audio-btn"
          data-audio-src=(input.data.sceneAudioSrc || "")
          data-audio-debug=(input.data.sceneAudioDebugMessage || "")
          title=(input.data.locale.audioBadgeLabel || "")
          aria-label=(input.data.locale.audioBadgeLabel || "")
        >▶</button>
      </if>
    </div>

    <div class="ll-zipf-panel ll-zipf-panel--scene">
      <label class="ll-zipf-toggle">
        <input type="checkbox" id="ll-zipf-highlight-toggle"/>
        <span class="ll-zipf-toggle__text">${input.data.textToolsLabels?.zipfHighlightLabel || ""}</span>
      </label>
      <div class="ll-zipf-source" role="group" aria-labelledby="ll-zipf-freq-legend-adv-classic">
        <div class="ll-zipf-source__legend" id="ll-zipf-freq-legend-adv-classic">${input.data.textToolsLabels?.lexiconFrequencySourceGroupLabel || ""}</div>
        <div class="ll-zipf-source__radios">
          <label class="ll-zipf-source__opt">
            <input type="radio" name="ll-lexicon-frequency-source" value="combined"/>
            <span>${input.data.textToolsLabels?.lexiconFrequencyCombinedLabel || ""}</span>
          </label>
          <label class="ll-zipf-source__opt">
            <input type="radio" name="ll-lexicon-frequency-source" id="ll-lexicon-frequency-wordfreq" value="wordfreq"/>
            <span>${input.data.textToolsLabels?.lexiconFrequencyWordfreqLabel || ""}</span>
          </label>
          <label class="ll-zipf-source__opt">
            <input type="radio" name="ll-lexicon-frequency-source" id="ll-lexicon-frequency-corpus" value="corpus"/>
            <span>${input.data.textToolsLabels?.lexiconFrequencyCorpusLabel || ""}</span>
          </label>
          <label class="ll-zipf-source__opt">
            <input type="radio" name="ll-lexicon-frequency-source" value="dwds"/>
            <span>${input.data.textToolsLabels?.lexiconFrequencyDwdsLabel || ""}</span>
          </label>
        </div>
      </div>
      <div class="ll-zipf-legend" id="ll-zipf-legend" hidden aria-hidden="true">
        <p class="ll-zipf-legend__title">${input.data.textToolsLabels?.zipfLegendTitle || ""}</p>
        <ul class="ll-zipf-legend__list" id="ll-zipf-legend-list"></ul>
      </div>
    </div>

    <div class="scene-script reader-text" id="ll-scene-text-root">
      <for|block| of=(input.data.scene.script || []).filter((b) => b.type !== "options")>
        <if=block.type === "narration">
          <div class="scene-block scene-block--narration">
            <p class="scene-block__text">${joinLines(block.text)}</p>
          </div>
        </if>
        <else-if=block.type === "dialog">
          <div class="scene-block scene-block--dialog">
            <div class="scene-block__speaker">${getCharName(block.character, input.data.adventure?.characters)}</div>
            <div class="scene-block__bubble">
              <p class="scene-block__text">${joinLines(block.text)}</p>
            </div>
          </div>
        </else-if>
      </for>
    </div>

    <if=((input.data.scene.script || []).find((b) => b.type === "options"))>
      <div class="scene-options">
        <p class="scene-options__label">${input.data.locale.classicNextLabel}</p>
        <for|opt| of=((input.data.scene.script || []).find((b) => b.type === "options")?.items || [])>
          <if=(input.data.sceneIdToSlug || {})[opt.nextId]>
            <a href=`${input.data.basePath}/${input.data.adventure.slug}/${(input.data.sceneIdToSlug || {})[opt.nextId]}` class="scene-option">
              ${opt.text}
            </a>
          </if>
          <else>
            <span class="scene-option" style="opacity:0.5; cursor:not-allowed">
              ${opt.text}
            </span>
          </else>
        </for>
      </div>
    </if>
    <else>
      <div class="scene-nav">
        <a href=`${input.data.basePath}/${input.data.adventure.slug}` class="btn btn--outline">${input.data.locale.classicBackOverviewLabel}</a>
        <div style="font-size:0.85rem; color:var(--color-text-muted); font-style:italic">
          ${input.data.locale.classicEndSceneLabel}
        </div>
      </div>
    </else>

    <if=input.data.scene.modules || input.data.scene.exercises>
      <hr class="divider" style="margin-top:var(--space-2xl)">
      <div style="margin-top:var(--space-xl)">
        <h2 style="font-size:1.1rem; font-weight:700; margin-bottom:var(--space-md)">${input.data.locale.exercisesTitle}</h2>
        <p style="color:var(--color-text-muted); font-size:0.95rem">
          ${input.data.locale.exercisesDescription}
        </p>
        <if=(input.data.premiumGate && input.data.premiumGate.blocked)>
          <div class="ll-exercise-lock">
            <div class="ll-exercise-lock__icon">🔒</div>
            <a class="btn btn--premium btn--sm" href=(input.data.premiumGate?.paymentLinks?.personal || "#")>${input.data.premiumGate?.labels?.exerciseLock?.unlockCta}</a>
          </div>
        </if>
      </div>
    </if>

    <div class="ll-adventure-scene__backdrop" id="ll-scene-word-backdrop" aria-hidden="true"></div>
    <div class="ll-adventure-scene__word-panel" id="ll-scene-word-panel" aria-hidden="true" role="dialog" aria-modal="false">
      <button type="button" id="ll-scene-word-close" class="ll-adventure-scene__word-close">✕</button>
      <button type="button" id="ll-scene-word-expand" class="ll-adventure-scene__word-expand">⛶</button>
      <div class="ll-word-infobox ll-word-infobox--overlay" id="ll-scene-word-infobox">
        <p class="ll-word-infobox__empty" id="ll-scene-word-infobox-empty"></p>
      </div>
    </div>
    <div class="ta-translate-tooltip" id="ll-scene-translate-tooltip">
      <span class="ta-translate-tooltip__translated" id="ll-scene-tooltip-translated"></span>
      <span class="ta-translate-tooltip__hint" id="ll-scene-tooltip-hint" hidden></span>
    </div>
    <html-script id="ll-scene-classic-data" type="application/json">
      $!{JSON.stringify({
        sceneId: input.data.scene.contentId || "",
        sceneImagePath: input.data.sceneImagePath || "",
        sceneAudioSrc: input.data.sceneAudioSrc || "",
        sceneScript: input.data.sceneScript || null,
        lang: input.data.targetLang || "",
        uiLang: input.data.uiLang || "",
        lexiconOccurrencesJsonUrl: "",
        lexiconTextUrl: "",
        lexiconDataLocale: input.data.targetLang || "",
        lexiconSectionKey: "scenes",
        lexiconApiPath: lexiconOccurrencesApiPath(input.data.targetLang || "de", String(input.data.scene.contentId || "").trim()),
        lexiconApiOrigin: input.data.lexiconApiOrigin || "",
        contentApiOrigin: input.data.contentApiOrigin || "",
        lexiconApiSpeakLang: lexiconApiSpeakLangForUi(input.data.uiLang),
        targetLang: input.data.targetLang || "",
        sceneHotspotsUrl: sceneHotspotsStaticJsonPath(input.data.targetLang || "", input.data.scene.contentId || ""),
        labels: input.data.textToolsLabels || {},
        interactables: input.data.scene.interactables || {},
        scriptBlocks: (input.data.scene.script || []).filter((b) => b.type !== "options"),
        characters: input.data.adventure?.characters || [],
        premiumGate: input.data.premiumGate || null,
      })}
    </html-script>
    <html-script src="/js/adventure-scene-client.js" defer></html-script>

    <PremiumPaywallCard data=(input.data.premiumGate || null)/>
  </div>
</else>

